import React from "react";
import { Field, Panel, StateChip } from "@/components/ldc01/Ldc01Ui";
import { NP } from "@/lib/ldc01";

const TargetTable = ({ manifest, observed }) => {
  const rows = manifest?.collections || [];
  if (!rows.length) return <p className="text-sm text-zinc-500 italic" data-testid="ldc01-targets-empty">{NP}.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="ldc01-targets-table">
        <thead><tr className="text-left overline"><th className="py-2 pr-4">Collection</th><th className="py-2 pr-4 text-right">Manifest</th><th className="py-2 text-right">Latest scan observed</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.collection} className="border-t border-[var(--border)]" data-testid={`ldc01-target-row-${r.collection}`}>
              <td className="py-2 pr-4 font-mono text-zinc-200">{r.collection}</td>
              <td className="py-2 pr-4 text-right font-mono text-white">{r.count}</td>
              <td className="py-2 text-right font-mono text-zinc-400">{observed?.[r.collection]?.count ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const List = ({ items, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {(items || []).map((x) => <span key={x} className="px-2 py-0.5 rounded-sm border border-[var(--border)] text-[11px] font-mono text-zinc-300">{x}</span>)}
  </div>
);

export function ReviewStep({ status, evidenceJob, manifest, approval, coordination }) {
  const ok = manifest?.integrity === "PASS";
  const p = manifest?.protected;
  const last = (status?.jobs || [])[0];
  const rr = coordination?.recovery_required;
  return (
    <div className="space-y-5" data-testid="ldc01-review-step">
      <Panel title="Cleanup scope" subtitle="Read-only. Values come from the server's pinned manifest; nothing here can change it.">
        <Field label="Cleanup job reference" value="LDC01" mono testId="ldc01-job-ref" />
        <Field label="Manifest version" value={manifest?.name || NP} muted={!manifest?.name} testId="ldc01-manifest-version" />
        <Field label="Manifest SHA-256" value={manifest?.sha256 || status?.manifest_sha256 || NP} mono testId="ldc01-manifest-sha" />
        <Field label="Manifest integrity (server)" value={!manifest ? NP : ok ? "Hash verified by server" : `FAILED — ${manifest.reason}`} testId="ldc01-manifest-ok" />
        <Field label="Manifest status" value={manifest?.draft_status || NP} muted={!manifest?.draft_status} testId="ldc01-manifest-draft" />
        <Field label="Owner approval" value={approval ? <span className="inline-flex items-center gap-2"><StateChip state={approval.status === "MALFORMED" ? "FAIL" : approval.status === "PENDING" ? "PENDING" : "NOT VERIFIED"} />{approval.status} · {approval.reason}</span> : NP} testId="ldc01-manifest-approval" />
        <Field label="Total targeted records" value={ok ? manifest.total : NP} mono muted={!ok} testId="ldc01-total-targets" />
        <Field label="Total collections" value={ok ? manifest.collection_count : NP} mono muted={!ok} testId="ldc01-total-collections" />
      </Panel>
      <Panel title="Collection-wise targets" subtitle={`Manifest counts (server). Observed column from ${evidenceJob ? `${evidenceJob.kind} ${evidenceJob.id}` : "no scan yet"}.`}>
        <TargetTable manifest={ok ? manifest : null} observed={evidenceJob?.report?.target_fingerprints} />
      </Panel>
      <Panel title="Protected modules & excluded records">
        {p ? (
          <div className="space-y-3" data-testid="ldc01-protected">
            <div><div className="overline mb-1">Protected collections</div><List items={p.protected_collections} testId="ldc01-protected-collections" /></div>
            <div><div className="overline mb-1">Master data collections</div><List items={p.master_collections} testId="ldc01-master-collections" /></div>
            <div><div className="overline mb-1">Excluded by directive ({p.exclusion_count})</div><List items={p.exclusion_directives} testId="ldc01-exclusions" /></div>
            <div className="text-xs text-zinc-500">FG baseline guard: {p.fg_baseline_guard ? "on" : "off"} · reference scan: {p.reference_scan}</div>
          </div>
        ) : <p className="text-sm text-zinc-500 italic" data-testid="ldc01-protected-np">{NP}.</p>}
      </Panel>
      <Panel title="Service state">
        <Field label="Service activation" value="Enabled (server answered)" testId="ldc01-service-enabled" />
        <Field label="Execution setting" value={status?.execute_enabled ? "ENABLED on server" : "Disabled on server"} testId="ldc01-execute-enabled" />
        <Field label="Job lock" value={!coordination ? NP : coordination.lock ? `Held by ${coordination.lock.job_id} (${coordination.lock.kind}) · ${coordination.lock.age_s ?? "?"}s` : "Free"} mono testId="ldc01-lock" />
        <Field label="Recovery required" value={!coordination ? NP : rr ? `YES — ${rr.run_id}` : "No marker"} mono testId="ldc01-recovery" />
        <Field label="Current / last job" value={last ? `${last.kind} · ${last.status} · ${last.started_at}` : "No jobs recorded"} mono testId="ldc01-last-job" />
      </Panel>
    </div>
  );
}
